export type StrategyBlock =
  | { kind: 'p'; text: string }
  | { kind: 'accent'; text: string }
  | { kind: 'sub'; text: string }
  | { kind: 'list'; items: string[] }

export interface StrategySection {
  number: string
  title: string
  blocks: StrategyBlock[]
}

export const strategyMeta = {
  title: 'Campaign Strategy — Precision Phenotyping in DME',
  subtitle: 'Scientific-first engagement strategy for Denmark · three instalments, 15 screens',
  note: 'Internal document. Not part of the HCP-facing journey and not counted as an HCP interaction. It describes the prototype as built: three instalments mapped to the HCP archetypes, runnable individually or as one presentation.',
  coreIdea: 'Earn the treatment conversation through precision phenotyping.',
  coreSupport:
    'By beginning with independent scientific value rather than treatment promotion, the campaign seeks to build credibility with a highly selective specialist audience and create the foundation for a later discussion about inflammatory DME and more individualised clinical decision-making.',
  finalLine:
    'Before we talk about how DME is treated, we first need to look more precisely at what type of DME we are treating.',
}

export const strategySections: StrategySection[] = [
  {
    number: '01',
    title: 'Market context',
    blocks: [
      {
        kind: 'p',
        text: 'The Danish ophthalmology environment presents a particular engagement challenge. The specialist audience is small, highly scientific and selective in how it engages with pharmaceutical companies.',
      },
      {
        kind: 'p',
        text: 'Traditional promotional communication therefore risks being dismissed before the message has established sufficient clinical relevance.',
      },
      {
        kind: 'p',
        text: 'At the same time, treatment decisions in DME can involve meaningful patient burden. Where the ideal candidate is difficult to identify with confidence, clinicians may understandably be cautious about exposing patients to additional intervention without a sufficiently clear rationale.',
      },
      {
        kind: 'p',
        text: 'The communication challenge is therefore broader than product awareness. The first task is not to persuade ophthalmologists to consider a treatment. The first task is to create confidence that:',
      },
      { kind: 'accent', text: 'DME can be characterised more precisely.' },
    ],
  },
  {
    number: '02',
    title: 'Strategic opportunity',
    blocks: [
      {
        kind: 'p',
        text: 'Recent AI-assisted OCT research creates an opportunity to begin the conversation at a different point: diagnostic precision before treatment discussion.',
      },
      {
        kind: 'p',
        text: 'Rather than beginning with therapy, the campaign begins with OCT-based phenotyping. The Midena analysis demonstrates that familiar OCT variables can be analysed together to identify distinct morphological DME phenotypes.',
      },
      { kind: 'accent', text: 'From measuring fluid to recognising phenotype.' },
      {
        kind: 'p',
        text: 'The implication is not that a phenotype automatically determines treatment. Instead, the campaign introduces the idea that more precise treatment decisions begin with more precise characterisation of the patient.',
      },
    ],
  },
  {
    number: '03',
    title: 'Why the campaign is phased',
    blocks: [
      {
        kind: 'p',
        text: 'The campaign is deliberately divided into stages. The audience should first be given a reason to reconsider how DME is characterised before being asked to reconsider how it may be treated.',
      },
      { kind: 'sub', text: 'Phase 1 — Precision phenotyping' },
      {
        kind: 'p',
        text: 'Completely unbranded and purely scientific. Focus on OCT, AI-assisted analysis, DME heterogeneity, morphological phenotyping and precision diagnostics.',
      },
      {
        kind: 'p',
        text: 'Out of scope: Ozurdex, dexamethasone, implants, treatment switching, treatment superiority and specific treatment pathways.',
      },
      {
        kind: 'p',
        text: 'The objective is to establish that DME is not one morphological pattern. The desired response is: “Perhaps there is more information in the OCT than fluid volume alone.”',
      },
      { kind: 'sub', text: 'Phase 2 — Embed precision' },
      {
        kind: 'p',
        text: 'For the specialist who already accepts that phenotypes exist. The science becomes applied: the window of opportunity, what is and is not recoverable, a review rhythm per cluster, and how the phenotype is captured inside the read that already happens.',
      },
      {
        kind: 'p',
        text: 'The question progresses from “Can we phenotype DME more precisely?” to “When does it matter, and how do I actually do this on a Tuesday?”',
      },
      { kind: 'sub', text: 'Phase 3 — Ignite precision' },
      {
        kind: 'p',
        text: 'For clinics already practising precision medicine. The offer is infrastructure rather than persuasion: honest scope for AI-assisted segmentation, a pilot they could run on their own scans, and an openly aspirational look at predicting response.',
      },
      {
        kind: 'p',
        text: 'Across all three, never imply that the phenotype model itself dictates treatment.',
      },
    ],
  },
  {
    number: '04',
    title: 'Why this is an unusual teaser',
    blocks: [
      {
        kind: 'p',
        text: 'A conventional teaser is designed to generate awareness → curiosity → action. This teaser has a different strategic role.',
      },
      { kind: 'accent', text: 'Credibility → scientific curiosity → permission for the next conversation.' },
      {
        kind: 'p',
        text: 'The primary reward is scientific insight, not a product message. The teaser is closer to a short scientific thought-starter than a promotional communication. Its job is to:',
      },
      {
        kind: 'list',
        items: [
          'Earn attention',
          'Establish credibility',
          'Introduce a genuinely new scientific perspective',
          'Reduce resistance to future communication',
          'Create curiosity around inflammatory DME',
          'Prepare the audience for deeper educational content',
        ],
      },
      {
        kind: 'p',
        text: 'The absence of a product message is intentional. It is the strategy.',
      },
    ],
  },
  {
    number: '05',
    title: 'Snak 1 — Foundational · Spark precision',
    blocks: [
      { kind: 'sub', text: 'Page 1 — Reframe the OCT' },
      {
        kind: 'p',
        text: '“What if OCT could reveal phenotype — not just fluid?” Introduces the possibility that OCT may provide a richer morphological picture of DME, with the Midena data establishing immediate credibility. Strategic role: challenge the frame without criticising current practice.',
      },
      { kind: 'sub', text: 'Page 2 — Establish heterogeneity' },
      {
        kind: 'p',
        text: '“DME is not one morphological pattern.” Connects established clinical heterogeneity with the possibility of systematic phenotyping. Takeaway: same diagnosis, different phenotype.',
      },
      { kind: 'sub', text: 'Page 3 — Explain the inputs' },
      {
        kind: 'p',
        text: '“5 OCT variables. One clustering model.” Shows the phenotypes are based on familiar clinical variables — IRF · SRF · I-HRF · ELM · EZ — without teaching basic OCT anatomy. Strategic role: make the AI output clinically credible rather than opaque.',
      },
      { kind: 'sub', text: 'Page 4 — Deliver the reveal' },
      {
        kind: 'p',
        text: '“One diagnosis. Four phenotypes.” Shows the four morphological clusters identified through the combined OCT biomarker profiles, turning heterogeneity into something visually recognisable. They are morphological phenotype clusters — not treatment groups, treatment-response categories or validated disease stages.',
      },
      { kind: 'sub', text: 'Page 5 — Deeper scientific engagement' },
      {
        kind: 'p',
        text: '“Let’s look at a few together.” Converts scientific curiosity into a low-friction next step — book a phenotyping walkthrough. The next action remains scientific rather than promotional: bring your own scans, explore OCT-based phenotyping, discuss biomarker patterns, engage at your own pace.',
      },
    ],
  },
  {
    number: '06',
    title: 'The engagement layer',
    blocks: [
      {
        kind: 'p',
        text: 'Interaction is optional throughout. A specialist who prefers a direct scientific read is never forced through a question, and nothing later depends on having answered one.',
      },
      { kind: 'sub', text: 'Snak 1 — the hidden quiz' },
      {
        kind: 'p',
        text: 'A quiz button on the heterogeneity page opens the unmet-need question and its peer comparison. It provides interaction and an engagement signal without becoming the conceptual backbone of the instalment.',
      },
      { kind: 'sub', text: 'Snak 2 and 3 — Bayesian probes' },
      {
        kind: 'p',
        text: 'A three-step probe replaces the single-question format: state your prior before seeing anything, receive the evidence with your prior echoed back, then update your view against peer responses.',
      },
      {
        kind: 'p',
        text: 'Snak 2 probes expected band disruption in long-standing DME; Snak 3 probes inter-reader agreement on counting hyperreflective foci. Peer shares are illustrative engagement data, labelled as such.',
      },
      {
        kind: 'accent',
        text: 'Prior, evidence, updated view — the same loop the campaign is asking the audience to run on itself.',
      },
    ],
  },
  {
    number: '07',
    title: 'Why this approach fits the audience',
    blocks: [
      {
        kind: 'accent',
        text: 'For a small, highly specialised audience, credibility is more valuable than promotional intensity.',
      },
      {
        kind: 'p',
        text: 'Instead of asking the audience to believe a treatment claim, the first interaction asks them to consider published OCT evidence. Instead of beginning with a product, it begins with a clinical question. Instead of telling ophthalmologists how to treat, it gives them a new way to think about how DME may be characterised.',
      },
      {
        kind: 'p',
        text: 'The campaign must first demonstrate: “This is worth my attention as a clinician.” Only later should it ask: “What could this mean for treatment?”',
      },
    ],
  },
  {
    number: '08',
    title: 'Mapping to the HCP archetypes',
    blocks: [
      {
        kind: 'p',
        text: 'The adoption model segments by audience maturity, not by time: Foundational, Developing and Advanced specialists exist simultaneously. Each instalment is therefore written for one archetype and carries two layers — a market-shaping spine and a short differentiation tail.',
      },
      {
        kind: 'list',
        items: [
          'Snak 1 · Foundational · Spark precision · differentiation: Preserve · completely unbranded',
          'Snak 2 · Developing · Embed precision · differentiation: Prevent · brand colours, no logo',
          'Snak 3 · Advanced · Ignite precision · differentiation: Predict · fully branded',
        ],
      },
      {
        kind: 'accent',
        text: 'Branding is not a slow reveal over time. It is a layer that each archetype carries differently.',
      },
      {
        kind: 'p',
        text: 'Sequence remains available but is no longer the only path. An entry menu lets a specialist enter at their own archetype, or lets a representative run all three instalments as one face-to-face presentation.',
      },
    ],
  },
  {
    number: '09',
    title: 'Snak 2 — Developing · Embed precision',
    blocks: [
      {
        kind: 'p',
        text: 'This reader already accepts that phenotypes exist. Persuasion is finished; the task is to add the time axis and make phenotyping part of the routine.',
      },
      { kind: 'sub', text: 'Page 1 — The window of opportunity' },
      {
        kind: 'p',
        text: '“The window closes quietly.” Outer retinal integrity declines through sustained exposure while acuity still looks acceptable, so structure changes before function complains.',
      },
      { kind: 'sub', text: 'Page 2 — Irreversibility' },
      {
        kind: 'p',
        text: 'Fluid volume responds to treatment; ELM and EZ continuity do not reliably recover. A CL 2 versus CL 4 comparison makes the threshold visible. Takeaway: treat the fluid, read the bands.',
      },
      { kind: 'sub', text: 'Page 3 — Proactive management of clusters' },
      {
        kind: 'p',
        text: 'Each phenotype gets a review rhythm — what to watch, at what interval, and what would prompt action. This is what converts recognition into proactive management. Rhythms are explicitly illustrative.',
      },
      { kind: 'sub', text: 'Page 4 — Embed it in the read' },
      {
        kind: 'p',
        text: 'No new equipment, scan or visit: read five variables instead of one, name the phenotype in the record, and set the interval to the phenotype. “What changes on Tuesday” is answered directly.',
      },
      { kind: 'sub', text: 'Page 5 — Differentiation tail · Prevent' },
      {
        kind: 'p',
        text: 'The timing argument becomes the product argument: address the inflammatory arm while the architecture is intact. Brand colours only, no logo lockup, claims flagged as pending medical and regulatory approval.',
      },
      { kind: 'accent', text: 'Snak 2 answers “when”, and then “how do I actually do this”.' },
    ],
  },
  {
    number: '10',
    title: 'Snak 3 — Advanced · Ignite precision',
    blocks: [
      {
        kind: 'p',
        text: 'This reader is already practising precision medicine. Selling the concept back to them is condescending; the offer is infrastructure and participation.',
      },
      { kind: 'sub', text: 'Page 1 — Precision medicine principles' },
      {
        kind: 'p',
        text: 'Right patient, right intervention, right time, measured — each mapped to something the specialist already does. The gap named is instrumentation, not conviction.',
      },
      { kind: 'sub', text: 'Page 2 — AI fit-to-clinic' },
      {
        kind: 'p',
        text: 'An honest does / does-not table. Consistent segmentation and foci counting on one side; diagnosis, therapy choice and self-validation firmly on the other.',
      },
      { kind: 'sub', text: 'Page 3 — Pilot AI in routine practice' },
      {
        kind: 'p',
        text: 'A concrete, finishable pilot: 50 retrospective scans, eight weeks, anonymised under a local data agreement, returning the clinic’s own cluster distribution and inter-reader comparison.',
      },
      { kind: 'sub', text: 'Page 4 — Differentiation tail · Predict' },
      {
        kind: 'p',
        text: 'Openly aspirational. The predictive question is posed as a hypothesis with status chips — not established, no predictive claim, research question — and the full brand lockup appears here for the first time.',
      },
      { kind: 'sub', text: 'Page 5 — Co-create' },
      {
        kind: 'p',
        text: 'The ask is participation rather than conversion: join the pilot, contribute to the Danish dataset, join the peer faculty.',
      },
      {
        kind: 'accent',
        text: 'Precision medicine adoption is not a message. It is a practice.',
      },
    ],
  },
  {
    number: '11',
    title: 'Two delivery modes',
    blocks: [
      { kind: 'sub', text: 'Independent campaigns' },
      {
        kind: 'p',
        text: 'Each instalment stands alone, entered directly from the menu. It opens on its own archetype framing, ends on its own differentiation tail and next step, and never depends on having seen the others.',
      },
      { kind: 'sub', text: 'One long presentation' },
      {
        kind: 'p',
        text: 'Run in sequence for face-to-face use, the three instalments read as a single 15-screen argument with chapter handovers between them and a running screen count for the presenter.',
      },
      {
        kind: 'p',
        text: 'Because branding escalates by phase, the presentation also carries its own compliance logic: the unbranded material can be shown to anyone, and the branded tail only appears once the scientific case has been made.',
      },
    ],
  },
  {
    number: '12',
    title: 'The design gradient',
    blocks: [
      {
        kind: 'p',
        text: 'Trust requirement falls as evidence accumulates; brand presence rises. The design follows the same curve, so the visual register itself signals what kind of communication the reader is in.',
      },
      { kind: 'sub', text: 'Snak 1 — clinical' },
      {
        kind: 'p',
        text: 'White surface, near-monochrome navy type, unmodified OCT imagery on black. Nothing that reads as marketing. No brand marks of any kind.',
      },
      { kind: 'sub', text: 'Snak 2 — applied' },
      {
        kind: 'p',
        text: 'Lightly tinted brand surface, brand blue and green used structurally rather than decoratively, a macro eye and colour fundus photograph warming the imagery. Product named in the tail; no logo lockup.',
      },
      { kind: 'sub', text: 'Snak 3 — branded' },
      {
        kind: 'p',
        text: 'Dark navy canvas, full-bleed imagery running to the edge of the screen, uppercase display headlines with green second lines, colour-blocked comparisons and a green primary action. The logo is present in the header and on the Predict page.',
      },
      {
        kind: 'accent',
        text: 'Flipping between the three should feel like three different kinds of document, not three colourways.',
      },
    ],
  },
  {
    number: '13',
    title: 'Tools in the prototype',
    blocks: [
      { kind: 'sub', text: 'Entry menu' },
      {
        kind: 'p',
        text: 'The opening screen keeps the choice behind a single button. Behind it: the three archetypes with their audience, verb, differentiation word and branding level, plus the option to run all three as one presentation.',
      },
      { kind: 'sub', text: 'The journey' },
      {
        kind: 'p',
        text: 'A visualisation of the arc from revealing the phenotype to precision treatment, showing each stage’s palette, its scientific weight against its brand presence, and the trust, register, design and ask that follow from them.',
      },
      { kind: 'sub', text: 'Storyboard' },
      {
        kind: 'p',
        text: 'All 15 screens as live miniature renders grouped by instalment, each with its strategic purpose and a ruled comments box. Prints to A4 as a PDF for review, with reviewer, function and date fields and one instalment per page.',
      },
      { kind: 'sub', text: 'This document' },
      {
        kind: 'p',
        text: 'Available from every screen. Internal only, and never counted as an HCP interaction.',
      },
    ],
  },
]
